import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * 路由级初始 Tab 上下文：canonical 子路由（如 /quality/rules）通过 Provider 注入目标 Tab，
 * 页面无需感知路径即可选中正确 Tab；Tab 切换仍沿用页面既有 ?tab= 逻辑。
 */
const TabRouteContext = createContext<string | null>(null);

export function TabRouteProvider({ tab, children }: { tab: string; children: ReactNode }) {
  return <TabRouteContext.Provider value={tab}>{children}</TabRouteContext.Provider>;
}

/** useBccTabUrl 选项 */
export interface UseBccTabUrlOptions<T extends string> {
  /** URL 中 param 缺失、或值不在 allowed 内时的回退 Tab（页面层按角色算好再传入，如 quality 的 defaultTab） */
  defaultValue: T;
  /** 合法 Tab 值集合（页面层按角色过滤后传入，如 opscenter 的 TABS 全部 key、quality 坐席仅 ['appeals']）。
   *  缺省（undefined）= 不校验：任意非空 URL 值原样生效、不回头校正（保留 quality 管理端现状——
   *  管理端全量可见，非法深链原值保留、URL 不自动修正）。 */
  allowed?: readonly T[];
  /** URL query 参数名，默认 'tab' */
  param?: string;
  /** 切到 defaultValue 时是否清空整个 query（quality 现状：默认 Tab 落点不带 ?tab=、且清掉其它参数）。
   *  false（opscenter 现状）：任何切换都写 param=key，含默认值本身。默认 false */
  clearOnDefault?: boolean;
  /** 忽略路由级 Tab 上下文（页内二级 tab 用：路由注入的是一级分组，二级 tab 不应读取它，
   *  否则分组 key 与子 tab key 撞名时（如报表分组 ai）会误选中。默认 false */
  ignoreRouteTab?: boolean;
}

/**
 * 页面级大 Tab 的「?tab= URL 同步 + 默认值/越权回退」公共逻辑
 * （抽取自 quality / opscenter 两页逐行同构样板，语义按两页现状复刻，两页差异收敛为 options）：
 *
 * - 初始值：param 缺失 → defaultValue；值非法 / 不在 allowed → defaultValue；否则原值生效。
 *   回退只改显示值、不回头改写 URL（与两页现状一致：非法深链 URL 保持原样）。
 * - **allowed 换代**（同一组件被多条路由复用、按分组渲染不同页签集）时，当前值若已不属于本组，
 *   一样回落到 defaultValue——这时 URL 多半不带 param（侧栏菜单是裸路径），只认「URL 变了才回灌」
 *   会让界面停在「无选中」。
 * - 外部 URL 变化（浏览器前进/后退、通知/深链跳转、历史替换）回灌 state：param 非空时按上述
 *   规则同步；param 缺失则保持当前 Tab 不动（quality 现状，不强行回默认）。
 * - setTab(key)：state 立即更新；URL 以 replace 同步（history.replaceState 语义，不产生额外历史，
 *   写 URL 时只增删本 param、**保留其它 query 参数**，切到默认 Tab 的 URL 形态由 clearOnDefault 区分。
 *
 * 返回 [tab, setTab]：tab 为受控显示值；setTab 接受任意 string（键均来自页面可见的 TabPane，
 * 与两页现状的 switchTab / setTab 签名一致，可直接作为 BccTabs onChange）。
 */
export function useBccTabUrl<T extends string = string>({
  defaultValue,
  allowed,
  param = 'tab',
  clearOnDefault = false,
  ignoreRouteTab = false,
}: UseBccTabUrlOptions<T>): [T, (key: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const ctxTab = useContext(TabRouteContext);
  const routeTab = ignoreRouteTab ? null : ctxTab;

  // 初始值：路由级 Tab（canonical 子路由）优先；否则 URL 缺失/非法 → 回退 defaultValue
  const [tab, setTabState] = useState<T>(() => {
    if (routeTab && (!allowed || allowed.includes(routeTab as T))) return routeTab as T;
    const raw = searchParams.get(param);
    if (!raw) return defaultValue;
    if (allowed && !allowed.includes(raw as T)) return defaultValue;
    return raw as T;
  });

  // URL 外部变化回灌（浏览器前进/后退、深链跳转、历史替换）；路由级 Tab 变化时同步。
  //
  // 护栏一：**只有 URL 真的变了才回灌**。`allowed`/`defaultValue` 由调用方传入，很多页面每帧新建数组
  // （`allowed={visible.map(...)}`），会让本 effect 在每次渲染都跑一遍；而此时 `searchParams` 可能还是
  // 「上一次导航尚未提交」的旧快照，于是用旧值把刚切过去的 Tab 又写回来——表现为「切回默认 Tab 时
  // URL 已清空、界面却停在上一个 Tab，必须再点一次」。用 URL 签名做闸门即可消除该竞态，
  // 且不改变「param 缺失则保持当前 Tab 不动」的既有语义。
  //
  // 护栏二：**URL 没变也要收敛「当前值已不属于本组」的情形**——一个组件被多条路由复用、按分组渲染
  // 不同页签集时（如 AI 中台 /platform/ai/{providers,cost,settings} 共用 AiCenter），换分组时 URL
  // 多半不带 ?tab=（侧栏菜单是裸路径），上一分组的 key 也不在本组 allowed 里；只认「URL 变了才回灌」
  // 会让 activeTab 停在本组并不存在的 key 上——Arco Tabs 对此没有兜底：整条 Tab 栏无高亮、内容区
  // 空白，且来回切得越多越容易命中（quality 用 key={seg}、report 用分组 effect 各自绕过过这只坑）。
  const lastSearchRef = useRef<string>(searchParams.toString());
  useEffect(() => {
    if (routeTab && (!allowed || allowed.includes(routeTab as T))) {
      setTabState((prev) => (prev === routeTab ? prev : (routeTab as T)));
      return;
    }
    const sig = searchParams.toString();
    const urlChanged = sig !== lastSearchRef.current;
    lastSearchRef.current = sig;
    const raw = searchParams.get(param);
    setTabState((prev) => {
      // 当前值非法（换分组/按权限裁剪后 allowed 变小）先落到本组默认值，再让 URL 的显式取值覆盖它
      const base = allowed && !allowed.includes(prev) ? defaultValue : prev;
      if (!urlChanged || !raw) return base === prev ? prev : base;
      const next = allowed && !allowed.includes(raw as T) ? defaultValue : (raw as T);
      return next === prev ? prev : next;
    });
  }, [searchParams, param, allowed, defaultValue, routeTab]);

  // 切 Tab：state 立即更新，URL 按页面现状 replace 同步（不改历史栈）；
  // 保留当前其它 query（如知识库 ?kb= 联动参数），仅增删本 tab 参数；
  // 用 window.location.search（最新值）比较，同值不调用 setSearchParams——
  // setSearchParams 即便内容相同也会导航，若路由按 location.key 重挂载会形成死循环。
  const setTab = (key: string) => {
    setTabState(key as T);
    const target = clearOnDefault && key === defaultValue ? null : key;
    const cur = new URLSearchParams(window.location.search).get(param);
    if (cur === target) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (target === null) {
        next.delete(param);
      } else {
        next.set(param, key);
      }
      return next;
    }, { replace: true });
  };

  return [tab, setTab];
}
